/**
 * SlideIndexSelector Component
 * Component to select which slide from a PowerPoint presentation
 * Features:
 * - Numeric input with increment/decrement buttons
 * - Visual slide thumbnail grid (if available)
 * - Slide range validation
 */

import { useState, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  ChevronUp,
  ChevronDown,
  Grid3X3,
  Check,
  Presentation,
} from 'lucide-react'

// ============================================
// Types
// ============================================

export interface SlideIndexSelectorProps {
  value: number
  onChange: (index: number) => void
  slideCount?: number
  thumbnails?: string[]
  disabled?: boolean
  className?: string
}

// ============================================
// SlideIndexSelector Component
// ============================================

export function SlideIndexSelector({
  value,
  onChange,
  slideCount = 0,
  thumbnails = [],
  disabled = false,
  className,
}: SlideIndexSelectorProps) {
  const [isGridOpen, setIsGridOpen] = useState(false)

  // Calculate max slide index (0-based)
  const maxIndex = slideCount > 0 ? slideCount - 1 : 0

  // Handle increment
  const handleIncrement = () => {
    if (value < maxIndex) {
      onChange(value + 1)
    }
  }

  // Handle decrement
  const handleDecrement = () => {
    if (value > 0) {
      onChange(value - 1)
    }
  }

  // Handle grid selection
  const handleGridSelect = (index: number) => {
    onChange(index)
    setIsGridOpen(false)
  }

  // Generate placeholder thumbnails if not provided
  const displayThumbnails = useMemo(() => {
    if (thumbnails.length > 0) return thumbnails
    // Generate placeholder array based on slide count
    return Array.from({ length: slideCount }, () => '')
  }, [thumbnails, slideCount])

  return (
    <div className={cn('space-y-2', className)}>
      <Label className="flex items-center gap-2">
        <Presentation className="h-4 w-4 text-neutral-500" />
        Slide Number
        {slideCount > 0 && (
          <span className="text-neutral-400 font-normal">
            (of {slideCount} slides)
          </span>
        )}
      </Label>

      <div className="flex items-center gap-2">
        {/* Numeric Input with Increment/Decrement */}
        <div className="flex items-center border border-neutral-200 dark:border-neutral-700 rounded-xl overflow-hidden bg-white dark:bg-neutral-900">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleDecrement}
            disabled={disabled || value <= 0}
            className="h-10 w-10 rounded-none border-r border-neutral-200 dark:border-neutral-700"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>

          <Input
            type="number"
            value={value + 1} // Display 1-based index
            onChange={(e) => {
              const displayValue = parseInt(e.target.value, 10)
              if (!isNaN(displayValue) && displayValue >= 1 && displayValue <= slideCount) {
                onChange(displayValue - 1) // Convert back to 0-based
              }
            }}
            disabled={disabled}
            min={1}
            max={slideCount}
            className="h-10 w-20 border-0 rounded-none text-center focus-visible:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleIncrement}
            disabled={disabled || value >= maxIndex}
            className="h-10 w-10 rounded-none border-l border-neutral-200 dark:border-neutral-700"
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
        </div>

        {/* Grid View Button */}
        {slideCount > 1 && (
          <Popover open={isGridOpen} onOpenChange={setIsGridOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={disabled}
                className="h-10 w-10 rounded-xl"
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[320px] p-0 rounded-xl"
              align="end"
              sideOffset={5}
            >
              <div className="p-3 border-b border-neutral-200 dark:border-neutral-700">
                <p className="text-sm font-medium text-neutral-900 dark:text-white">
                  Select Slide
                </p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  Click on a slide to select it
                </p>
              </div>

              <ScrollArea className="max-h-[280px]">
                <div className="grid grid-cols-3 gap-2 p-3">
                  {displayThumbnails.map((thumbnail, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleGridSelect(index)}
                      className={cn(
                        'relative aspect-video rounded-lg overflow-hidden',
                        'border-2 transition-all',
                        'focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1',
                        value === index
                          ? 'border-orange-500'
                          : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600'
                      )}
                    >
                      {thumbnail ? (
                        <img
                          src={thumbnail}
                          alt={`Slide ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                          <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
                            {index + 1}
                          </span>
                        </div>
                      )}

                      {/* Selection Indicator */}
                      {value === index && (
                        <div className="absolute top-1 right-1 bg-orange-500 rounded-full p-0.5">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      )}

                      {/* Slide Number Badge */}
                      <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                        {index + 1}
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Helper Text */}
      {slideCount > 0 && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          Selected: Slide {value + 1} of {slideCount}
        </p>
      )}
    </div>
  )
}

export default SlideIndexSelector
